import { useEffect, useState } from "react";
import api from "../api";
import toast from "../utils/toast";
import "./RolesManager.css";

// Etiquetas para mostrar en los checkboxes de accesos
const PERMISOS_LABELS = {
  pos: "Panel Principal (POS)",
  dashboard_pedidos: "Dashboard pedidos",
  usuarios: "Usuarios",
  productos: "Productos",
  pedidos: "Pedidos",
  cierres: "Cierres de caja",
  movimientos: "Movimientos",
  arqueos: "Arqueos",
  informes: "Informes",
  roles: "Roles",
  insumos: "Insumos",
  inventario: "Inventario",
  horarios: "Horarios",
};

const PERMISOS_FALLBACK = Object.keys(PERMISOS_LABELS);

function normalizarPermisos(value) {
  if (Array.isArray(value)) return value.filter((p) => typeof p === "string");
  if (typeof value !== "string") return [];
  return value
    .replace(/[{}"]/g, "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
}

function normalizarRol(rol = {}) {
  return { ...rol, permisos: normalizarPermisos(rol.permisos) };
}

function mezclarPermisos(permisos = []) {
  return Array.from(new Set([...PERMISOS_FALLBACK, ...normalizarPermisos(permisos)]));
}

export default function RolesManager() {
  const [roles, setRoles] = useState([]);
  const [permisosDisponibles, setPermisosDisponibles] = useState([]);
  const [nuevo, setNuevo] = useState({
    codigo: "",
    nombre: "",
    descripcion: "",
    permisos: [],
  });
  const [editando, setEditando] = useState(null);
  const [err, setErr] = useState("");

  const cargar = async () => {
    try {
      setErr("");
      const { data } = await api.get("/api/roles");
      setRoles(Array.isArray(data) ? data.map(normalizarRol) : []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    }
  };

  useEffect(() => {
    cargar();
    api.get("/api/roles/permisos")
      .then(({ data }) => {
        setPermisosDisponibles(mezclarPermisos(data));
      })
      .catch(() => setPermisosDisponibles(mezclarPermisos()));
  }, []);

  const permisosCatalogo = mezclarPermisos(permisosDisponibles);

  const sincronizarUsuarioActual = (rolActualizado, codigoAnterior) => {
    try {
      const raw = localStorage.getItem("usuario");
      const usuario = raw ? JSON.parse(raw) : null;
      if (!usuario || usuario.rol !== codigoAnterior) return;
      const actualizado = {
        ...usuario,
        rol: rolActualizado.codigo,
        permisos: normalizarPermisos(rolActualizado.permisos),
      };
      localStorage.setItem("usuario", JSON.stringify(actualizado));
      window.dispatchEvent(new CustomEvent("usuario-actualizado", { detail: actualizado }));
    } catch (_) {}
  };

  const togglePermisoNuevo = (codigo) => {
    setNuevo((n) => ({
      ...n,
      permisos: n.permisos.includes(codigo)
        ? n.permisos.filter((p) => p !== codigo)
        : [...n.permisos, codigo],
    }));
  };

  const togglePermisoEditando = (codigo) => {
    setEditando((e) => ({
      ...e,
      permisos: (e.permisos || []).includes(codigo)
        ? (e.permisos || []).filter((p) => p !== codigo)
        : [...(e.permisos || []), codigo],
    }));
  };

  const agregar = async () => {
    if (!nuevo.codigo.trim() || !nuevo.nombre.trim()) {
      return toast("Completa código y nombre");
    }
    try {
      await api.post("/api/roles/crear", {
        ...nuevo,
        permisos: normalizarPermisos(nuevo.permisos),
      });
      setNuevo({ codigo: "", nombre: "", descripcion: "", permisos: [] });
      cargar();
    } catch (e) {
      toast(e?.response?.data?.error || "Error al agregar rol");
    }
  };

  const guardarEdicion = async (rol) => {
    try {
      const codigoAnterior = roles.find((r) => r.id === rol.id)?.codigo || rol.codigo;
      const permisosEditados = normalizarPermisos(rol.permisos);
      const { data } = await api.put(`/api/roles/${rol.id}`, {
        codigo: rol.codigo,
        nombre: rol.nombre,
        descripcion: rol.descripcion ?? "",
        activo: rol.activo,
        permisos: permisosEditados,
      });
      const rolActualizado = { ...normalizarRol(data?.role || rol), permisos: permisosEditados };
      sincronizarUsuarioActual(rolActualizado, codigoAnterior);
      setEditando(null);
      setRoles((prev) => prev.map((r) => (r.id === rol.id ? rolActualizado : r)));
      await cargar();
    } catch (e) {
      toast(e?.response?.data?.error || "Error al actualizar");
    }
  };

  const toggleActivo = async (r) => {
    try {
      await api.put(`/api/roles/${r.id}`, {
        ...r,
        activo: !r.activo,
        permisos: normalizarPermisos(r.permisos),
      });
      cargar();
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo actualizar");
    }
  };

  const eliminar = async (r) => {
    if (!window.confirm(`¿Eliminar el rol "${r.nombre}" (${r.codigo})?`)) return;
    try {
      await api.delete(`/api/roles/${r.id}`);
      setEditando(null);
      cargar();
    } catch (e) {
      toast(e?.response?.data?.error || "No se pudo eliminar");
    }
  };

  return (
    <div className="rm-wrapper">
      <div className="rm-card">
        <div className="rm-header">
          <div>
            <h2>Mantenedor de Roles</h2>
            <p className="rm-subtitle">
              Crea y edita roles. El código se usa al asignar rol a un usuario (ej: superadmin, vendedor).
            </p>
          </div>
          <span className="rm-badge">{roles.length} rol{roles.length !== 1 ? "es" : ""}</span>
        </div>

        <div className="rm-form">
          <div className="rm-field">
            <label>Código</label>
            <input
              placeholder="ej: cajero"
              value={nuevo.codigo}
              onChange={(e) =>
                setNuevo({ ...nuevo, codigo: e.target.value.replace(/\s+/g, "_").toLowerCase() })
              }
            />
          </div>
          <div className="rm-field">
            <label>Nombre</label>
            <input
              placeholder="Ej: Cajero"
              value={nuevo.nombre}
              onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })}
            />
          </div>
          <div className="rm-field rm-field-wide">
            <label>Descripción (opcional)</label>
            <input
              placeholder="Breve descripción del rol"
              value={nuevo.descripcion}
              onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })}
            />
          </div>
          <div className="rm-actions">
            <button className="rm-btn rm-btn-primary" onClick={agregar}>
              Agregar rol
            </button>
          </div>
        </div>

        <div className="rm-permisos-section">
          <label className="rm-permisos-title">Accesos del nuevo rol</label>
          <div className="rm-permisos-grid">
            {permisosCatalogo.map((cod) => (
              <label key={cod} className="rm-check-label">
                <input
                  type="checkbox"
                  checked={nuevo.permisos.includes(cod)}
                  onChange={() => togglePermisoNuevo(cod)}
                />
                <span>{PERMISOS_LABELS[cod] ?? cod}</span>
              </label>
            ))}
          </div>
        </div>

        {err && <div className="rm-error">Error: {err}</div>}

        <div className="rm-table-wrap">
          <table className="rm-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Accesos</th>
                <th>Estado</th>
                <th className="rm-col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id}>
                  {editando?.id === r.id ? (
                    <>
                      <td>
                        <input
                          className="rm-inline-input"
                          value={editando.codigo}
                          onChange={(e) =>
                            setEditando({
                              ...editando,
                              codigo: e.target.value.replace(/\s+/g, "_").toLowerCase(),
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="rm-inline-input"
                          value={editando.nombre}
                          onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="rm-inline-input"
                          value={editando.descripcion || ""}
                          onChange={(e) =>
                            setEditando({ ...editando, descripcion: e.target.value })
                          }
                        />
                      </td>
                      <td className="rm-permisos-cell">
                        <div className="rm-permisos-grid rm-permisos-grid-inline">
                          {permisosCatalogo.map((cod) => (
                            <label key={cod} className="rm-check-label">
                              <input
                                type="checkbox"
                                checked={(editando.permisos || []).includes(cod)}
                                onChange={() => togglePermisoEditando(cod)}
                              />
                              <span>{PERMISOS_LABELS[cod] ?? cod}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>—</td>
                      <td className="rm-col-actions">
                        <button
                          className="rm-btn rm-btn-ghost"
                          onClick={() => guardarEdicion(editando)}
                        >
                          Guardar
                        </button>
                        <button
                          className="rm-btn rm-btn-ghost"
                          onClick={() => setEditando(null)}
                        >
                          Cancelar
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        <span className="rm-tag">{r.codigo}</span>
                      </td>
                      <td>{r.nombre}</td>
                      <td className="rm-desc">{r.descripcion || "—"}</td>
                      <td className="rm-desc">
                        {(r.permisos || []).length
                          ? (r.permisos || []).map((p) => PERMISOS_LABELS[p] || p).join(", ")
                          : "—"}
                      </td>
                      <td>
                        <span
                          className={
                            r.activo ? "rm-pill rm-pill-ok" : "rm-pill rm-pill-off"
                          }
                        >
                          {r.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="rm-col-actions">
                        <button
                          className="rm-btn rm-btn-ghost"
                          onClick={() => toggleActivo(r)}
                        >
                          {r.activo ? "Desactivar" : "Activar"}
                        </button>
                        <button
                          className="rm-btn rm-btn-ghost"
                          onClick={() => setEditando({ ...r, permisos: r.permisos ? [...r.permisos] : [] })}
                        >
                          Editar
                        </button>
                        <button
                          className="rm-btn rm-btn-danger"
                          onClick={() => eliminar(r)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={6} className="rm-empty">
                    No hay roles. Agrega uno arriba.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
